// components/NotificationConfigurationSelector.tsx
import React, { useState } from 'react';
import {
    Select,
    Card,
    Space,
    Tag,
    Button,
    Modal,
    Typography,
    Alert,
    Divider,
    Tooltip,
    Badge,
    Empty,
    Spin,
    List,
    Checkbox
} from 'antd';
import {
    NotificationOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    WarningOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { NotificationConfigurationDTO } from '@/shared/api/model';
import { useNotificationConfigurations, notificationConfigUtils } from '../../hooks/useNotificationConfigurations';
import {NotificationConfigurationSelection} from "@/modules/action-flow/config";

const { Option, OptGroup } = Select;
const { Text, Paragraph } = Typography;

interface NotificationConfigurationSelectorProps {
    value?: NotificationConfigurationSelection[];
    onChange?: (configurations: NotificationConfigurationSelection[]) => void;
    placeholder?: string;
    disabled?: boolean;
    maxCount?: number;
    showPreview?: boolean;
}

export const NotificationConfigurationSelector: React.FC<NotificationConfigurationSelectorProps> = ({
                                                                                                        value = [],
                                                                                                        onChange,
                                                                                                        placeholder = "Select notification configurations",
                                                                                                        disabled = false,
                                                                                                        maxCount,
                                                                                                        showPreview = true
                                                                                                    }) => {
    const { configurations, loading, error, getConfigurationByCode } = useNotificationConfigurations();
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [selectedConfigForPreview, setSelectedConfigForPreview] = useState<NotificationConfigurationDTO | null>(null);

    // Get selected codes
    const selectedCodes = value.map(config => config.code);

    // Validate selected configurations
    const validation = notificationConfigUtils.validateCodes(selectedCodes, configurations);

    // Group configurations by platform for better organization
    const groupedConfigurations = notificationConfigUtils.groupByPlatform(
        configurations.filter(config => config.active)
    );

    const handleSelectionChange = (codes: string[]) => {
        if (maxCount && codes.length > maxCount) {
            // Don't allow selection beyond maxCount
            return;
        }

        const selections: NotificationConfigurationSelection[] = codes
            .map(code => {
                const config = getConfigurationByCode(code);
                return config ? notificationConfigUtils.toSelection(config) : null;
            })
            .filter(Boolean) as NotificationConfigurationSelection[];

        onChange?.(selections);
    };

    const handlePreview = (config: NotificationConfigurationDTO) => {
        setSelectedConfigForPreview(config);
        setPreviewModalVisible(true);
    };

    const renderOption = (config: NotificationConfigurationDTO) => (
        <Option key={config.code} value={config.code}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space>
                    <Tag
                        color={notificationConfigUtils.getPlatformColor(config.platform)}
                    >
                        {config.platform}
                    </Tag>
                    <Text>{config.name}</Text>
                    <Text type="secondary">({config.code})</Text>
                </Space>
                {showPreview && (
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePreview(config);
                        }}
                    />
                )}
            </Space>
        </Option>
    );

    if (loading) {
        return <Spin><Select placeholder="Loading configurations..." disabled /></Spin>;
    }

    if (error) {
        return (
            <div>
                <Select placeholder="Error loading configurations" disabled />
                <Alert
                    message="Error loading notification configurations"
                    description={error}
                    type="error"
                    style={{ marginTop: 4 }}
                />
            </div>
        );
    }

    return (
        <div>
            <Select
                mode="multiple"
                placeholder={placeholder}
                value={selectedCodes}
                onChange={handleSelectionChange}
                disabled={disabled}
                style={{ width: '100%' }}
                optionFilterProp="children"
                filterOption={(input, option) => {
                    const config = getConfigurationByCode(option?.value as string);
                    if (!config) return false;

                    const searchText = input.toLowerCase();
                    return (
                        config.name.toLowerCase().includes(searchText) ||
                        config.code.toLowerCase().includes(searchText) ||
                        config.platform.toLowerCase().includes(searchText)
                    );
                }}
                maxCount={maxCount}
                suffixIcon={<NotificationOutlined />}
            >
                {Object.entries(groupedConfigurations).map(([platform, configs]) => (
                    <OptGroup
                        key={platform}
                        label={
                            <Space>
                                <Tag color={notificationConfigUtils.getPlatformColor(platform)}>
                                    {platform}
                                </Tag>
                                <Text type="secondary">({configs.length} configurations)</Text>
                            </Space>
                        }
                    >
                        {configs.map(renderOption)}
                    </OptGroup>
                ))}
            </Select>

            {/* Validation warnings */}
            {!validation.valid && validation.invalidCodes.length > 0 && (
                <Alert
                    message="Invalid Configuration Codes"
                    description={`The following codes are no longer valid: ${validation.invalidCodes.join(', ')}`}
                    type="warning"
                    icon={<WarningOutlined />}
                    style={{ marginTop: 8 }}
                    action={
                        <Button
                            size="small"
                            onClick={() => handleSelectionChange(validation.validCodes)}
                        >
                            Remove Invalid
                        </Button>
                    }
                />
            )}

            {/* Selected configurations display */}
            {value.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Selected ({value.length}):
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        {value.map((config) => {
                            const fullConfig = getConfigurationByCode(config.code);
                            const isValid = !!fullConfig;

                            return (
                                <Tooltip
                                    key={config.code}
                                    title={
                                        isValid
                                            ? `${fullConfig.name} - ${fullConfig.description || 'No description'}`
                                            : 'Configuration not found or inactive'
                                    }
                                >
                                    <Tag
                                        style={{ marginBottom: 4, marginRight: 4 }}
                                        color={isValid ? notificationConfigUtils.getPlatformColor(config.platform) : 'red'}
                                        icon={isValid ? <CheckCircleOutlined /> : <WarningOutlined />}
                                    >
                                        {config.name}
                                    </Tag>
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            <Modal
                title={
                    <Space>
                        <NotificationOutlined />
                        <span>Notification Configuration Preview</span>
                    </Space>
                }
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setPreviewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                {selectedConfigForPreview && (
                    <div>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card size="small">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Space>
                                        <Text strong>Code:</Text>
                                        <Tag>{selectedConfigForPreview.code}</Tag>
                                    </Space>
                                    <Space>
                                        <Text strong>Name:</Text>
                                        <Text>{selectedConfigForPreview.name}</Text>
                                    </Space>
                                    <Space>
                                        <Text strong>Platform:</Text>
                                        <Tag color={notificationConfigUtils.getPlatformColor(selectedConfigForPreview.platform)}>
                                            {selectedConfigForPreview.platform}
                                        </Tag>
                                    </Space>
                                    <Space>
                                        <Text strong>Status:</Text>
                                        <Badge
                                            status={selectedConfigForPreview.active ? "success" : "error"}
                                            text={selectedConfigForPreview.active ? "Active" : "Inactive"}
                                        />
                                    </Space>
                                    {selectedConfigForPreview.description && (
                                        <div>
                                            <Text strong>Description:</Text>
                                            <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                                                {selectedConfigForPreview.description}
                                            </Paragraph>
                                        </div>
                                    )}
                                </Space>
                            </Card>

                            <Card size="small" title="Technical Details">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Text strong>Template:</Text>
                                        <Text style={{ marginLeft: 8 }}>{selectedConfigForPreview.templateName}</Text>
                                    </div>
                                    <div>
                                        <Text strong>Context Script ID:</Text>
                                        <Text style={{ marginLeft: 8 }}>{selectedConfigForPreview.contextScriptId}</Text>
                                    </div>
                                    <div>
                                        <Text strong>Language:</Text>
                                        <Text style={{ marginLeft: 8 }}>{selectedConfigForPreview.language}</Text>
                                    </div>
                                </Space>
                            </Card>
                        </Space>
                    </div>
                )}
            </Modal>
        </div>
    );
};